import { Box, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import * as yup from 'yup'

import api from 'api'
import { AuthStateContext } from 'context/auth'
import PromiseHandler from 'components/Common/PromiseHandler'
import ResendCodeMessage from 'components/Common/ResendCodeMessage'
import ZendeskLink from 'components/Common/ZendeskLink'
import FormikTextField from 'components/Common/FormikTextField'
import Button from 'components/Common/Button'
import { styled } from '@mui/material/styles'
import { fetchToken } from 'store/actions/authActions'
import { useSelector } from 'react-redux'
import { checkCookie, setCookie } from 'utils/helper'

const errorVerificationCodeMessage = 'Invalid verification code, please check your email and try again.'

const schema = yup.object({
  code: yup.string().min(6, 'Code must be at least 6 characters').required('Code is required'),
})


const Title = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  lineHeight: '20px',
  fontSize: '24px',
  fontWeight: 600,
}))



export const AccountVerificationForm = ({ onNextStep, resendCode }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [authState, authDispatch] = useContext(AuthStateContext)
  const { email, code, password, firstName, lastName } = authState
  const formik = useRef(null)
  const [confirmEmailPromise, setPromise] = useState(null)
  const isEpsg = useSelector(state => state.authReducer.EpsgUser)

  const initialValue = useMemo(() => ({ code: code || '' }), [code])

  const onFormSubmit = useCallback(
    values => {
      if (email && password) {
        const promise = api.confirmSignUpEmail(email.toLowerCase(), values.code)
        setPromise(promise)
      }
    },
    [email, password],
  )

  const onConfirmEmailSuccess = useCallback(async () => {
    if (email && password) {
      try {
        let awsUser = await api.signIn(email.toLowerCase(), password)

        if (!firstName || !lastName) {
          const _firstName = awsUser.attributes['custom:firstName']
          const _lastName = awsUser.attributes['custom:lastName']
          isEpsg &&
            fetchToken({
              email,
              password,
              ep: isEpsg,
              firstName: _firstName,
              lastName: _lastName,
            })

          const createUserRes = await api.createUser({ firstName: _firstName, lastName: _lastName, accountType: isEpsg ? 'premium' : 'free' })
          await api.createBusinessDefault({
            business_name: `${_firstName}_${_lastName}_${moment().unix()}`,
            cognito_user_id: createUserRes['id'] || '',
            ep: isEpsg ? isEpsg : null
          })
        } else {

          isEpsg &&
            fetchToken({
              email,
              password,
              ep: isEpsg,
              firstName: firstName,
              lastName: lastName,
            })

          const createUserRes = await api.createUser({ firstName, lastName, accountType: isEpsg ? 'premium' : 'free' })

          await api.createBusinessDefault({
            business_name: `${firstName}_${lastName}_${moment().unix()}`,
            cognito_user_id: createUserRes['id'] || '',
            ep: isEpsg ? isEpsg : null
          })
        }

        const user = await api.getUser()
        user.defaultBusinessCreated = true
        dispatch({ type: 'userUpdated', user })
        authDispatch({ type: 'signUpCompleted' })
        // console.log('pass-code-user-data-', user);
        if (checkCookie(user.email, user.id)) {
          // console.log('pass-code-user-data-true');
          navigate('/');
        } else {
          setCookie(user.email, user.id, 365)
          // console.log('pass-code-user-data-false',);
          navigate('/walkthrough');
        }
      } catch (error) {
        console.log('Confirm error: ', error)
      }
    }
  }, [navigate, authDispatch, dispatch, email, password, firstName, lastName, isEpsg])
  // }, [onNextStep])

  const onPromiseError = useCallback(error => {
    switch (error.name) {
      case 'CodeMismatchException':
        formik.current?.setErrors({ code: errorVerificationCodeMessage })
        break

      default: {
        formik.current?.setErrors({ code: error.message })
      }
    }
    formik.current?.setSubmitting(false)
  }, [])

  return (
    <>
      <PromiseHandler promise={confirmEmailPromise} then={onConfirmEmailSuccess} catch={onPromiseError} />
      <Box>
        <Typography variant="h5">
          <Title>Enter your confirmation code</Title>
        </Typography>
        <Box mb={4}>
          <Typography sx={{ fontSize: '14px' }}>
            Your accountsd is not activated yet. For security purposes, please check your email <b>{email}</b> to
            activate your account and get started with Markaaz.
          </Typography>
        </Box>
      </Box>
      <Formik innerRef={formik} initialValues={initialValue} onSubmit={onFormSubmit} validationSchema={schema}>
        {({ isSubmitting, isValid }) => {
          return (
            <>
              <Form>
                <FormikTextField name="code" label="Confirmation Code" fullWidth autoFocus type="password" />
                <Box mt={3} mb={5}>
                  <ResendCodeMessage resendCode={resendCode} />
                </Box>
                <Box textAlign='center'>
                  <Button loading={isSubmitting} sx={{ width: '250px' }} submit>
                    Submit
                  </Button>
                </Box>
              </Form>
              <ZendeskLink />
            </>
          )
        }}
      </Formik>
    </>
  )
}
